import { useState, useEffect, useCallback, useRef } from 'react';
import { useFetchQueueContext } from '../context/FetchQueueContext';

/**
 * Hook for widgets to fetch data through the queue system
 *
 * @param {string} widgetId - Unique identifier for the widget
 * @param {Object} config - Fetch configuration
 * @param {Function} config.apiCall - Function that returns a promise (axios request)
 * @param {string} [config.cacheKey] - Optional cache key for deduplication
 * @param {number} [config.cacheTtl] - Optional cache TTL in ms (default: 30000)
 * @param {boolean} [config.enabled=true] - Whether to fetch (for conditional fetching)
 * @param {Function} [config.transform] - Optional function to transform response data
 *
 * @returns {Object} { data, isLoading, error, refresh }
 */
export const useFetchQueue = (widgetId, config) => {
    const {
        apiCall,
        cacheKey,
        cacheTtl,
        enabled = true,
        transform,
    } = config;

    const { registerFetch, unregisterWidget, queueRefresh, patchCache } = useFetchQueueContext();

    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Keep a ref synced to the latest data so mutate() can read it without a side
    // effect inside the setData updater (which double-invokes under StrictMode).
    const dataRef = useRef(data);
    dataRef.current = data;

    // Track if component is mounted and if initial fetch has been done
    const isMountedRef = useRef(true);
    const fetchIdRef = useRef(0);
    const hasFetchedRef = useRef(false);

    // Store config in refs to avoid dependency changes triggering re-fetches
    const configRef = useRef({ apiCall, cacheKey, cacheTtl, transform });
    configRef.current = { apiCall, cacheKey, cacheTtl, transform };

    // Cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            unregisterWidget(widgetId);
        };
    }, [widgetId, unregisterWidget]);

    // Perform fetch
    const doFetch = useCallback(async (isRefresh = false) => {
        if (!enabled) {
            setIsLoading(false);
            return;
        }

        const currentFetchId = ++fetchIdRef.current;
        setIsLoading(true);
        setError(null);

        const { apiCall: currentApiCall, cacheKey: currentCacheKey, cacheTtl: currentCacheTtl, transform: currentTransform } = configRef.current;

        try {
            const fetchConfig = {
                apiCall: currentApiCall,
                cacheKey: currentCacheKey,
                cacheTtl: currentCacheTtl,
            };

            const result = isRefresh
                ? await queueRefresh(widgetId, fetchConfig)
                : await registerFetch(widgetId, fetchConfig);

            // Only update state if this is still the latest fetch and component is mounted
            if (isMountedRef.current && currentFetchId === fetchIdRef.current) {
                const transformedData = currentTransform ? currentTransform(result) : result;
                setData(transformedData);
                setIsLoading(false);
            }
        } catch (err) {
            // Only update state if this is still the latest fetch and component is mounted
            if (isMountedRef.current && currentFetchId === fetchIdRef.current) {
                // Don't set error for cancellation (unmount)
                if (err.message !== 'Widget unmounted') {
                    setError(err.message || 'Failed to load data');
                }
                setIsLoading(false);
            }
        }
    }, [widgetId, enabled, registerFetch, queueRefresh]);

    // Initial fetch - only run once when enabled
    useEffect(() => {
        if (enabled && !hasFetchedRef.current) {
            hasFetchedRef.current = true;
            doFetch(false);
        }
    }, [enabled, doFetch]);

    // Refresh function for polling or manual refresh
    const refresh = useCallback(() => {
        doFetch(true);
    }, [doFetch]);

    const mutate = useCallback((updater) => {
        const next = typeof updater === 'function' ? updater(dataRef.current) : updater;
        dataRef.current = next;
        setData(next);
        const { cacheKey } = configRef.current;
        if (cacheKey) patchCache(cacheKey, next);
    }, [patchCache]);

    return {
        data,
        isLoading,
        error,
        refresh,
        mutate,
    };
};

/**
 * Hook for widgets that need multiple fetches
 * Returns an array of fetch results in the same order as configs
 *
 * @param {string} widgetId - Unique identifier for the widget
 * @param {Array<Object>} configs - Array of fetch configurations
 *
 * @returns {Object} { results: Array<{data, isLoading, error}>, refresh, isLoading, error }
 */
export const useMultiFetchQueue = (widgetId, configs) => {
    const { registerFetch, unregisterWidget, queueRefresh } = useFetchQueueContext();

    const [results, setResults] = useState(() =>
        configs.map(() => ({ data: null, isLoading: true, error: null }))
    );

    const isMountedRef = useRef(true);
    const fetchIdRef = useRef(0);
    const hasFetchedRef = useRef(false);

    // Store configs in ref to avoid triggering re-fetches
    const configsRef = useRef(configs);
    configsRef.current = configs;

    // Check if all configs are enabled
    const allEnabled = configs.every(c => c.enabled !== false);

    // Cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            // Unregister all sub-fetches
            const currentConfigs = configsRef.current;
            currentConfigs.forEach((_, index) => {
                unregisterWidget(`${widgetId}-${index}`);
            });
        };
    }, [widgetId, unregisterWidget]);

    // Perform all fetches
    const doFetch = useCallback(async (isRefresh = false) => {
        const currentConfigs = configsRef.current;

        if (!allEnabled) {
            setResults(currentConfigs.map(() => ({ data: null, isLoading: false, error: null })));
            return;
        }

        const currentFetchId = ++fetchIdRef.current;

        // Set all to loading
        setResults(currentConfigs.map(() => ({ data: null, isLoading: true, error: null })));

        // Execute fetches sequentially (they'll be queued anyway, but this ensures order)
        const newResults = [];

        for (let i = 0; i < currentConfigs.length; i++) {
            const config = currentConfigs[i];
            const subWidgetId = `${widgetId}-${i}`;

            try {
                const fetchConfig = {
                    apiCall: config.apiCall,
                    cacheKey: config.cacheKey,
                    cacheTtl: config.cacheTtl,
                };

                const result = isRefresh
                    ? await queueRefresh(subWidgetId, fetchConfig)
                    : await registerFetch(subWidgetId, fetchConfig);

                const transformedData = config.transform ? config.transform(result) : result;
                newResults.push({ data: transformedData, isLoading: false, error: null });
            } catch (err) {
                if (err.message !== 'Widget unmounted') {
                    newResults.push({ data: null, isLoading: false, error: err.message });
                } else {
                    newResults.push({ data: null, isLoading: false, error: null });
                }
            }
        }

        // Only update state if this is still the latest fetch and component is mounted
        if (isMountedRef.current && currentFetchId === fetchIdRef.current) {
            setResults(newResults);
        }
    }, [widgetId, allEnabled, registerFetch, queueRefresh]);

    // Initial fetch - only run once when enabled
    useEffect(() => {
        if (allEnabled && !hasFetchedRef.current) {
            hasFetchedRef.current = true;
            doFetch(false);
        }
    }, [allEnabled, doFetch]);

    // Refresh function
    const refresh = useCallback(() => {
        doFetch(true);
    }, [doFetch]);

    // Aggregate loading and error states
    const isLoading = results.some(r => r.isLoading);
    const error = results.find(r => r.error)?.error || null;

    return {
        results,
        refresh,
        isLoading,
        error,
    };
};

export default useFetchQueue;
